import { Link } from '@remix-run/react'
import {
  EXTERNAL_LINK_PROPS,
  getEncargaUnCocheUrl,
} from '#app/shared/lib/external-links.ts'
import { Icon } from '#app/shared/ui/icons/Icon.tsx'
import { useLegalModal } from '../data-display/LegalModalContext'
import { Separator } from '../data-display/Separator'
import { Spacer } from '../data-display/Spacer'
import Logo from './Logo'

type Props = {
  isFixed?: boolean
}

export default function Footer({ isFixed = false }: Props) {
  const { openModal } = useLegalModal()

  return (
    <footer
      className={`
        min-h-[753px] w-full bg-neutral-900 pb-7 pt-10

        lg:min-h-[284px] lg:pb-9 lg:pt-14

        ${isFixed ? 'lg:fixed lg:bottom-0 lg:left-0' : ''}
      `}
    >
      {/* TOP */}
      <div
        className={`
          justify-between px-4 pb-6

          lg:flex lg:pb-12

          xl:mx-auto xl:max-w-[1344px]
        `}
      >
        {/* LEFT - Logo and Description */}
        <div
          className={`
            flex flex-col items-center justify-center

            lg:max-w-[280px] lg:items-start
          `}
        >
          <Logo variant="light" />
          <p
            className={`
              mt-3 w-[359px] text-center text-subtitle1-medium text-white

              lg:w-full lg:text-left
            `}
          >
            Compra un coche en su punto óptimo o encárganos el modelo que
            buscas.
          </p>
        </div>

        <Spacer className="lg:hidden" size="40px" />

        {/* MIDDLE - Navigation Columns */}
        <div
          className={`
            mt-1 flex flex-col items-center justify-between gap-8

            lg:mt-0 lg:flex-row lg:items-start lg:gap-16
          `}
        >
          {/* Location Column */}
          <div
            className={`
              flex flex-col items-center gap-[15px]

              lg:items-start
            `}
          >
            <p className="h-[11px] text-base-bold text-white">
              Nuestra locación
            </p>
            <div className="flex items-start gap-2">
              <Icon
                name="basic/marker-pin"
                size="20px"
                className={`
                  mt-1 hidden text-white

                  lg:block
                `}
              />
              <div
                className={`
                  flex flex-col text-center

                  lg:max-w-[280px] lg:text-left
                `}
              >
                <p className="text-subtitle1-medium text-white">
                  Ronda General Mitre 38-40, Local comercial sótano 1, 08017
                  Barcelona
                </p>
                <p className="text-subtitle1-medium text-white opacity-80">
                  (Junto a Mooby Gran Sarrià Multicines)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon
                name="basic/phone-call"
                size="20px"
                className="text-white"
              />
              <p className="text-subtitle1-medium text-white">931 318 618</p>
            </div>
          </div>

          {/* Cars Column */}
          <div
            className={`
              flex flex-col items-center gap-[15px]

              lg:items-start
            `}
          >
            <p className="text-base-bold text-white">Nuestros coches</p>
            <Link to="/nuestros-coches">
              <p className="text-subtitle1-medium text-white">Comprar coche</p>
            </Link>
            {/* App externa: /encarga-un-coche ya no se usa en este repo */}
            <a href={getEncargaUnCocheUrl()} {...EXTERNAL_LINK_PROPS}>
              <p className="text-subtitle1-medium text-white">
                Solicitar coche
              </p>
            </a>
            <Link to="/vende-tu-coche">
              <p className="text-subtitle1-medium text-white">
                Vender tu coche
              </p>
            </Link>
            {/* <Link to="/financia">
              <p className="h-[10px] text-subtitle1-medium text-white">
                Financiación
              </p>
            </Link> */}
          </div>

          {/* About Column */}
          <div
            className={`
              flex flex-col items-center gap-[15px]

              lg:items-start
            `}
          >
            <p className="text-base-bold text-white">Gama Óptima</p>
            <Link to="/sobre-nosotros">
              <p className="text-subtitle1-medium text-white">Sobre nosotros</p>
            </Link>
            <Link to="/contacto">
              <p className="text-subtitle1-medium text-white">Contacto</p>
            </Link>
          </div>
        </div>

        <Spacer className="lg:hidden" size="72px" />

        {/* RIGHT - Social Media Icons */}
        <div
          className={`
            flex justify-center gap-1

            lg:flex-col
          `}
        >
          <div className="flex justify-start gap-1">
            <Link
              to="https://www.linkedin.com/company/gama-optima/?viewAsMember=true"
              className={`shrink-0`}
              target="_blank"
            >
              <img
                className="h-12 w-12 object-contain"
                src="/img/linkedin.png"
                alt="linkedin"
              />
            </Link>
            <Link
              to="https://x.com/GamaOptima"
              className="shrink-0"
              target="_blank"
            >
              <img
                className="h-12 w-12 object-contain"
                src="/img/x.png"
                alt="x"
              />
            </Link>
            <Link
              to="https://www.instagram.com/gamaoptima"
              className={`shrink-0`}
              target="_blank"
            >
              <img
                className="h-12 w-12 object-contain"
                src="/img/instagram.png"
                alt="instagram"
              />
            </Link>
          </div>
          <div className="flex justify-start gap-1">
            <Link
              to="https://www.facebook.com/profile.php?id=61583930866188"
              target="_blank"
            >
              <img
                className="h-12 w-12 object-contain"
                src="/img/fb.png"
                alt="facebook"
              />
            </Link>
            <Link to="https://www.tiktok.com/@gamaoptima" target="_blank">
              <img
                className="h-12 w-12 object-contain"
                src="/img/tiktok.png"
                alt="tiktok"
              />
            </Link>
            <Link to="https://www.youtube.com/@GamaOptima" target="_blank">
              <img
                className="h-12 w-12 object-contain"
                src="/img/youtube.png"
                alt="tiktok"
              />
            </Link>
          </div>
        </div>
      </div>

      {/* MIDDLE - Separator */}
      <div className="px-3">
        <Separator
          className={`!rounded-lg bg-line-gradient-light opacity-[37%]`}
        />
      </div>

      {/* BOTTOM - Copyright and Legal Links */}
      <div
        className={`
          flex flex-col items-center justify-between gap-6 px-4 pt-6

          lg:flex-row lg:items-start lg:gap-0

          xl:mx-auto xl:max-w-[1344px]
        `}
      >
        <p className="text-center text-subtitle2-medium text-white">
          © Gama Óptima {new Date().getFullYear()}. Todos los derechos
          reservados
        </p>

        <div
          className={`
            flex flex-wrap justify-center gap-3

            lg:flex-row lg:items-center lg:gap-6
          `}
        >
          <button
            className={`
              cursor-pointer text-subtitle2-medium text-white

              hover:underline
            `}
            onClick={() => openModal('terminos-y-condiciones')}
          >
            Términos y condiciones
          </button>
          <button
            className={`
              cursor-pointer text-subtitle2-medium text-white

              hover:underline
            `}
            onClick={() => openModal('politica-de-cookies')}
          >
            Política de cookies
          </button>
          <button
            className={`
              cursor-pointer text-subtitle2-medium text-white

              hover:underline
            `}
            onClick={() => openModal('politica-de-privacidad')}
          >
            Política de privacidad
          </button>
          <button
            className={`
              cursor-pointer text-subtitle2-medium text-white

              hover:underline
            `}
            onClick={() => openModal('aviso-legal')}
          >
            Aviso Legal
          </button>
        </div>
      </div>
    </footer>
  )
}
